import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Sheet, Typography, List, ListItem, ListItemButton, CircularProgress, IconButton, Stack, Avatar, Dropdown, Menu, MenuButton, MenuItem, Divider } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import { AdminThemeProvider, useAdminTheme } from '../../components/admin/theme.tsx';
import ChangeAdminPasswordModal from '../../components/admin/ChangeAdminPasswordModal.tsx';

const NAV_ITEMS = [
  { to: '/admin/stats', label: 'Statistics', icon: '📊' },
  { to: '/admin', label: 'Settings', icon: '⚙️', exact: true },
  { to: '/admin/users', label: 'Users', icon: '👥' },
  { to: '/admin/announcement', label: 'Announcement', icon: '📢' },
  { to: '/admin/admin-tokens', label: 'API Tokens', icon: '🔑' }
];

function AdminShell() {
  const { api, authenticated, username, logout } = useAdmin();
  const { mode, toggle } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { required } = await api.setupStatus();
        if (cancelled) return;
        if (required) { navigate('/admin/setup', { replace: true }); return; }
        if (!authenticated && location.pathname !== '/admin/login') {
          navigate('/admin/login', { replace: true });
        }
      } catch {
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, authenticated, navigate, location.pathname]);

  if (checking) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.body' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authenticated) return <Outlet />;

  const initials = (username || 'A').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '260px 1fr', height: '100vh', overflow: 'hidden', bgcolor: 'background.body' }}>
      <Sheet
        variant="plain"
        sx={{
          p: 2,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.surface',
          height: '100vh',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ px: 1, py: 2, mb: 1, flexShrink: 0 }}>
          <Typography level="h4" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>Admin</Typography>
          <Typography level="body-xs" color="neutral">Temp Mail Console</Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <List size="md" sx={{ '--ListItem-radius': '8px', '--List-gap': '4px' }}>
            {NAV_ITEMS.map(item => (
              <ListItem key={item.to}>
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  end={item.exact}
                  sx={{
                    fontWeight: 500,
                    '&.active': {
                      bgcolor: 'primary.softBg',
                      color: 'primary.plainColor',
                      fontWeight: 600
                    }
                  }}
                >
                  <span style={{ marginRight: 10 }}>{item.icon}</span>
                  {item.label}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
        <Divider sx={{ my: 1, flexShrink: 0 }} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 1, py: 1, flexShrink: 0 }}>
          <Avatar size="sm" color="primary">{initials}</Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-sm" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {username || '—'}
            </Typography>
            <Typography level="body-xs" color="neutral">Administrator</Typography>
          </Box>
          <Dropdown>
            <MenuButton slots={{ root: IconButton }} slotProps={{ root: { variant: 'plain', size: 'sm', color: 'neutral' } }}>
              ⋮
            </MenuButton>
            <Menu placement="top-end">
              <MenuItem onClick={toggle}>
                {mode === 'dark' ? '☀️ Light mode' : '🌙 Dark mode'}
              </MenuItem>
              <MenuItem onClick={() => setPwdOpen(true)}>🔒 Change password</MenuItem>
              <Divider />
              <MenuItem color="danger" onClick={() => { logout(); navigate('/admin/login', { replace: true }); }}>
                ⎋ Logout
              </MenuItem>
            </Menu>
          </Dropdown>
        </Stack>
      </Sheet>

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: { xs: 2, md: 4 }, py: 0 }}>
          <Outlet />
          <Box sx={{ height: 32 }} />
        </Box>
        <Box sx={{ flexShrink: 0, py: 1.5, px: 3, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.surface', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography level="body-xs" color="neutral">Temp Mail Admin Console</Typography>
          <Typography level="body-xs" color="neutral">v1.0</Typography>
        </Box>
      </Box>

      <ChangeAdminPasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </Box>
  );
}

export default function AdminLayout() {
  return (
    <AdminThemeProvider>
      <AdminShell />
    </AdminThemeProvider>
  );
}
